import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

export default function AdminI18n() {
  const [langs, setLangs] = useState([]);
  const [selected, setSelected] = useState("tr");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [addLangOpen, setAddLangOpen] = useState(false);
  const [newLangCode, setNewLangCode] = useState("");
  const [newLangName, setNewLangName] = useState("");
  const [legal, setLegal] = useState({ slug: "kvkk", title: "", content: "" });
  const [legalSlug, setLegalSlug] = useState("kvkk");

  const loadLangs = () => api.get("/admin/i18n/languages", adminApi()).then(({ data }) => setLangs(data));
  const loadItems = (code) => api.get(`/admin/i18n/${code}`, adminApi()).then(({ data }) => setItems(data));
  const loadLegal = (slug) => api.get(`/legal/${slug}`).then(({ data }) => setLegal({ slug, title: data.title || "", content: data.content || "" }));

  useEffect(() => { loadLangs(); }, []);
  useEffect(() => { loadItems(selected); }, [selected]);
  useEffect(() => { loadLegal(legalSlug); }, [legalSlug]);

  const save = async (key, value) => {
    try { await api.put("/admin/i18n", { lang: selected, key, value }, adminApi()); toast.success("Kaydedildi"); loadItems(selected); }
    catch { toast.error("Hata"); }
  };

  const addLang = async () => {
    if (!newLangCode) return;
    try {
      await api.post("/admin/i18n/languages", { code: newLangCode.toLowerCase(), name: newLangName || newLangCode }, adminApi());
      toast.success("Dil eklendi");
      setAddLangOpen(false); setNewLangCode(""); setNewLangName("");
      loadLangs();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const removeLang = async (code) => {
    if (!window.confirm(`${code} dili silinsin mi?`)) return;
    try { await api.delete(`/admin/i18n/languages/${code}`, adminApi()); toast.success("Silindi"); loadLangs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const filtered = items.filter((i) => !filter || i.key.toLowerCase().includes(filter.toLowerCase()) || (i.value || "").toLowerCase().includes(filter.toLowerCase()));

  const saveLegal = async () => {
    try { await api.put(`/admin/legal/${legal.slug}`, { title: legal.title, content: legal.content }, adminApi()); toast.success("Kaydedildi"); }
    catch { toast.error("Hata"); }
  };

  return (
    <div className="font-admin-body">
      <h1 className="font-admin-head text-3xl mb-2">Diller & Çeviriler</h1>
      <p className="text-sm text-white/60 mb-6">Frontend'de görünen tüm metinleri buradan yönetebilirsin. Yeni dil ekleyip aynı anahtarların çevirilerini gir.</p>

      <div className="admin-card p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="text-sm text-white/60">Diller:</div>
        {langs.map((l) => (
          <div key={l.code} className={`flex items-center gap-1 px-2 py-1 rounded ${selected === l.code ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-white/5"}`}>
            <button onClick={() => setSelected(l.code)} data-testid={`lang-${l.code}`} className="text-sm">
              {l.name} ({l.code}) {l.is_default && "★"}
            </button>
            {l.code !== "tr" && (
              <button onClick={() => removeLang(l.code)} className="text-red-400 opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            )}
          </div>
        ))}
        <button onClick={() => setAddLangOpen(true)} className="admin-btn-ghost text-xs px-3 py-1 flex items-center gap-1"><Plus className="w-3 h-3" /> Dil Ekle</button>
      </div>

      <div className="mb-3">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Anahtar veya metin ara..." className="admin-input max-w-md" />
      </div>

      <div className="admin-card overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-white/50 uppercase tracking-wider sticky top-0 bg-[#121212]">
            <tr className="border-b border-[#27272A]">
              <th className="p-3 w-64">Anahtar</th>
              <th className="p-3">Değer ({selected})</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <TranslationRow key={it.key} item={it} onSave={save} />
            ))}
            {filtered.length === 0 && <tr><td colSpan={3} className="p-6 text-white/50 text-center">Kayıt yok</td></tr>}
          </tbody>
        </table>
      </div>

      {addLangOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="admin-card p-6 w-full max-w-md">
            <h2 className="font-admin-head text-xl mb-4">Yeni Dil</h2>
            <div className="space-y-3">
              <div><label className="text-xs text-white/50">Kod (ör: ar, de, fr)</label><input value={newLangCode} onChange={(e) => setNewLangCode(e.target.value)} className="admin-input" /></div>
              <div><label className="text-xs text-white/50">Ad</label><input value={newLangName} onChange={(e) => setNewLangName(e.target.value)} placeholder="Deutsch" className="admin-input" /></div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={addLang} className="admin-btn flex-1">Ekle</button>
              <button onClick={() => setAddLangOpen(false)} className="admin-btn-ghost flex-1">İptal</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-admin-head text-2xl mb-3">Yasal Sayfalar</h2>
        <div className="flex gap-2 mb-3">
          {["kvkk", "kullanim"].map((s) => (
            <button key={s} onClick={() => setLegalSlug(s)} className={`px-4 py-2 rounded ${legalSlug === s ? "admin-btn" : "admin-btn-ghost"}`}>
              {s === "kvkk" ? "KVKK & Gizlilik" : "Kullanım Şartları"}
            </button>
          ))}
        </div>
        <div className="admin-card p-4 space-y-3">
          <div><label className="text-xs text-white/50">Başlık</label><input value={legal.title} onChange={(e) => setLegal({ ...legal, title: e.target.value })} className="admin-input" /></div>
          <div><label className="text-xs text-white/50">İçerik (Markdown desteklenir)</label><textarea value={legal.content} onChange={(e) => setLegal({ ...legal, content: e.target.value })} rows={16} className="admin-input font-mono text-xs" /></div>
          <button onClick={saveLegal} className="admin-btn flex items-center gap-2"><Save className="w-4 h-4" /> Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function TranslationRow({ item, onSave }) {
  const [val, setVal] = useState(item.value || "");
  const [dirty, setDirty] = useState(false);
  return (
    <tr className="border-b border-[#18181B]">
      <td className="p-3 font-mono text-xs text-white/60 align-top">{item.key}</td>
      <td className="p-3">
        <textarea value={val} onChange={(e) => { setVal(e.target.value); setDirty(true); }} rows={Math.max(1, Math.ceil(val.length / 60))} className="admin-input text-sm" />
      </td>
      <td className="p-3 align-top">
        <button disabled={!dirty} onClick={() => { onSave(item.key, val); setDirty(false); }} className={`admin-btn text-xs px-3 py-1 ${dirty ? "" : "opacity-40 cursor-not-allowed"}`}>Kaydet</button>
      </td>
    </tr>
  );
}
